import React from 'react';
import { Text } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { SprintCommitmentIndicator } from '@atlassian/jira-sprint-commitment-indicator/src/ui/index.tsx';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useFormatStoryPointsTotal } from '../../../common/hooks/use-format-story-points-total/index.tsx';
import {
	ESTIMATION_ROLLUP_BADGE_MAX,
	ISSUE_COUNT,
	ORIGINAL_ESTIMATE,
	STORY_POINTS,
} from '../../../model/constants.tsx';
import type { Estimations as EstimationsType } from '../../../model/estimations/types.tsx';
import type { EstimationType } from '../../../model/types.tsx';
import { FIELD_DEFAULT_VALUE } from '../card/card-contents/extra-field/constants.tsx';
import EstimationsBadge from './badge/index.tsx';
import messages, { type Message } from './messages.tsx';

export type EstimationsProps = {
	estimations: EstimationsType | null;
	estimationType: EstimationType;
	timeTrackingOptions: TimeTrackingOptions;
	isCompanyManaged: boolean;
	estimationName: string;
	totalEstimations: number;
	totalCards: number;
	columnCounts:
		| { initialColumnCount: number; inProgressColumnCount: number; doneColumnCount: number }
		| undefined;
	tooltipDelay?: number;
	rapidViewId: string;
	onToggleInsightsPanel: () => void;
	isShowingRecommendations: boolean;
};

type StatusDescription = {
	statusDescriptionMessage: Message;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	statusDescriptionMessageParams?: Record<string, any>;
};

export const Estimations = ({
	estimations,
	estimationType,
	timeTrackingOptions,
	isCompanyManaged,
	estimationName,
	totalEstimations,
	totalCards,
	columnCounts,
	tooltipDelay,
	rapidViewId,
	onToggleInsightsPanel,
	isShowingRecommendations,
}: EstimationsProps) => {
	const { formatMessage } = useIntl();

	const formatStoryPointsTotal = useFormatStoryPointsTotal();
	const formatEstimationValue = (value: number) => {
		if (estimationType === ORIGINAL_ESTIMATE) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return timeTrackingFormatter(value, timeTrackingOptions, {
				formatMessage,
			} as IntlShape);
		}

		// fix for floating point math issues (like 0.1 + 0.2 becomes 0.30000000000000004)
		// as the numbers end with lots of zeroes followed by a single digit, trimming
		// after 8 decimal points results in a sane representation
		return formatStoryPointsTotal(value);
	};

	const getTotal = () => {
		if (estimationType === ORIGINAL_ESTIMATE) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return timeTrackingFormatter(totalEstimations, timeTrackingOptions, {
				formatMessage,
			} as IntlShape);
		}
		return formatEstimationValue(estimationType === STORY_POINTS ? totalEstimations : totalCards);
	};

	const makeStatusWithoutDescription = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		statusMessage: any,
		estimationFormatted: string,
		total: string,
		tooltipTarget: string,
	) =>
		`${formatMessage(statusMessage)}: ${formatMessage(messages.tooltip, {
			count: estimationFormatted,
			total,
			estimationName: (isCompanyManaged && estimationName && estimationName !== FIELD_DEFAULT_VALUE
				? estimationName
				: tooltipTarget
			).toLowerCase(),
		})}`;

	const makeTooltipContent = (
		statusMessage: Message,
		total: string,
		tooltipTarget: string,
		estimationCount: string,
		statusDescription?: StatusDescription,
	) => {
		if (isCompanyManaged) {
			return (
				<>
					<Text as="strong" color="color.text.inverse">
						{formatMessage(statusMessage)}
					</Text>
					{statusDescription
						? ` (${formatMessage(statusDescription.statusDescriptionMessage, {
								...statusDescription.statusDescriptionMessageParams,
							})})`
						: null}
					<br />
					{formatMessage(messages.tooltip, {
						count: estimationCount,
						total,
						estimationName: (isCompanyManaged &&
						estimationName &&
						estimationName !== FIELD_DEFAULT_VALUE
							? estimationName
							: tooltipTarget
						).toLowerCase(),
					})}
				</>
			);
		}
		return makeStatusWithoutDescription(statusMessage, estimationCount, total, tooltipTarget);
	};

	const makeScreenReaderMessage = (
		statusMessage: Message,
		total: string,
		tooltipTarget: string,
		estimateCount: string,
		statusDescription?: StatusDescription,
	) => {
		if (isCompanyManaged) {
			return `${formatMessage(statusMessage)}${
				statusDescription
					? ` (${formatMessage(statusDescription.statusDescriptionMessage, {
							...statusDescription.statusDescriptionMessageParams,
						})})`
					: ''
			}: ${formatMessage(messages.tooltip, {
				count: estimateCount,
				total,
				estimationName: (isCompanyManaged &&
				estimationName &&
				estimationName !== FIELD_DEFAULT_VALUE
					? estimationName
					: tooltipTarget
				).toLowerCase(),
			})}`;
		}
		return makeStatusWithoutDescription(statusMessage, estimateCount, total, tooltipTarget);
	};

	const formatEstimation = (estimationValue: number | undefined) => {
		const value = estimationValue || 0;
		return estimationType === ORIGINAL_ESTIMATE
			? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				timeTrackingFormatter(value, timeTrackingOptions, {
					formatMessage,
				} as IntlShape)
			: formatEstimationValue(value);
	};

	const makeTooltipContentForEstimationGroups = () => {
		const { inProgressColumnCount } = columnCounts || {};
		const showStatusDescriptionForNotStartedAndDone =
			inProgressColumnCount && inProgressColumnCount > 0;

		const notStarted = {
			status: isCompanyManaged ? messages.notStartedV2 : messages.notStarted,
			statusDescription:
				isCompanyManaged && showStatusDescriptionForNotStartedAndDone
					? { message: messages.leftmostColumn }
					: undefined,
			estimationValue: formatEstimation(estimations?.NOT_STARTED),
		};

		const inProgress = {
			status: isCompanyManaged ? messages.inProgressV2 : messages.inProgress,
			statusDescription: undefined,
			estimationValue: formatEstimation(estimations?.IN_PROGRESS),
		};

		const done = {
			status: isCompanyManaged ? messages.completed : messages.done,
			statusDescription:
				isCompanyManaged && showStatusDescriptionForNotStartedAndDone
					? { message: messages.rightmostColumn }
					: undefined,
			estimationValue: formatEstimation(estimations?.DONE),
		};

		const tooltipTypes = Object.freeze({
			[ORIGINAL_ESTIMATE]: messages.originalEstimate,
			[STORY_POINTS]: messages.storyPoints,
			[ISSUE_COUNT]: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.issueCountIssueTermRefresh
				: messages.issueCount,
		});

		const tooltipTarget = formatMessage(
			tooltipTypes[estimationType] ||
				(expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.issueCountIssueTermRefresh
					: messages.issueCount),
		);
		const total = getTotal();

		return {
			notStarted,
			inProgress,
			done,
			tooltipTarget,
			total,
		};
	};

	if (estimations) {
		const { notStarted, inProgress, done, tooltipTarget, total } =
			makeTooltipContentForEstimationGroups();
		const { inProgressColumnCount, doneColumnCount } = columnCounts || {};
		return (
			<>
				<EstimationsBadge
					appearance="default"
					max={ESTIMATION_ROLLUP_BADGE_MAX}
					tooltipContent={makeTooltipContent(
						notStarted.status,
						total,
						tooltipTarget,
						notStarted.estimationValue,
						notStarted.statusDescription
							? { statusDescriptionMessage: notStarted.statusDescription.message }
							: undefined,
					)}
					tooltipDelay={tooltipDelay}
					screenReaderMessage={makeScreenReaderMessage(
						notStarted.status,
						total,
						tooltipTarget,
						notStarted.estimationValue,
						notStarted.statusDescription
							? { statusDescriptionMessage: notStarted.statusDescription.message }
							: undefined,
					)}
				>
					{formatEstimationValue(estimations.NOT_STARTED)}
				</EstimationsBadge>
				{((inProgressColumnCount && inProgressColumnCount > 0) || !isCompanyManaged) && (
					<EstimationsBadge
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							backgroundColor: token('color.border.information'),
						}}
						appearance="primary"
						max={ESTIMATION_ROLLUP_BADGE_MAX}
						tooltipContent={makeTooltipContent(
							inProgress.status,
							total,
							tooltipTarget,
							inProgress.estimationValue,
							undefined,
						)}
						tooltipDelay={tooltipDelay}
						screenReaderMessage={makeScreenReaderMessage(
							inProgress.status,
							total,
							tooltipTarget,
							inProgress.estimationValue,
							inProgress.statusDescription,
						)}
					>
						{formatEstimationValue(estimations.IN_PROGRESS)}
					</EstimationsBadge>
				)}
				{((doneColumnCount && doneColumnCount === 1) || !isCompanyManaged) && (
					<EstimationsBadge
						appearance={isVisualRefreshEnabled() ? 'added' : undefined}
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							backgroundColor: token('color.border.success'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							color: token('color.text.inverse'),
						}}
						max={ESTIMATION_ROLLUP_BADGE_MAX}
						tooltipContent={makeTooltipContent(
							done.status,
							total,
							tooltipTarget,
							done.estimationValue,
							done.statusDescription
								? { statusDescriptionMessage: done.statusDescription.message }
								: undefined,
						)}
						tooltipDelay={tooltipDelay}
						screenReaderMessage={makeScreenReaderMessage(
							done.status,
							total,
							tooltipTarget,
							done.estimationValue,
							done.statusDescription
								? { statusDescriptionMessage: done.statusDescription.message }
								: undefined,
						)}
					>
						{formatEstimationValue(estimations.DONE)}
					</EstimationsBadge>
				)}
				{isShowingRecommendations &&
				expValEquals('jira_insights_backlog_recommendations', 'cohort', 'variation') ? (
					<JSErrorBoundary
						fallback="unmount"
						id="sprintCommitmentIndicator"
						packageName="jiraSprintCommitmentIndicator"
						teamName="helios-reports"
					>
						<SprintCommitmentIndicator
							rapidViewId={rapidViewId}
							committed={totalEstimations}
							estimationType={
								estimationType === 'ORIGINAL_ESTIMATE' ? 'TIME_TRACKING' : estimationType
							}
							onToggleInsightsPanel={onToggleInsightsPanel}
						/>
					</JSErrorBoundary>
				) : null}
			</>
		);
	}

	return <UFOLoadHold name="estimations">{null}</UFOLoadHold>;
};
